/**
 * Generates initials from first and last name (or full name)
 * Handles cases where full name is in one field
 * @param firstName - The first name (or full name)
 * @param lastName - The last name (optional)
 * @returns Two character initials in uppercase (e.g., "MH" for MD Hoda)
 * @example
 * getInitials("John", "Doe") // "JD"
 * getInitials("MD Hoda") // "MH"
 * getInitials("MD", "Hoda") // "MH"
 * getInitials("John") // "JO" (if no last name, uses first and second word)
 * getInitials("", "") // "?"
 */
export const getInitials = (
  firstName?: string | null,
  lastName?: string | null
): string => {
  // Handle null, undefined, and empty/whitespace-only cases
  const sanitizeInput = (name: string | null | undefined): string => {
    if (!name) return "";
    return name.trim();
  };

  const sanitizedFirstName = sanitizeInput(firstName);
  const sanitizedLastName = sanitizeInput(lastName);

  // If both names are empty, return placeholder
  if (!sanitizedFirstName && !sanitizedLastName) {
    return "?";
  }

  // Combine names and split by whitespace to handle multi-word names
  const fullName = `${sanitizedFirstName} ${sanitizedLastName}`.trim();
  const nameParts = fullName.split(/\s+/).filter((part) => part.length > 0);

  // If we have parts, get first char of first and last word
  if (nameParts.length >= 2) {
    const firstInitial = nameParts[0][0].toUpperCase();
    const lastInitial = nameParts[nameParts.length - 1][0].toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  // If only one part, return single char or question mark
  if (nameParts.length === 1) {
    return nameParts[0][0].toUpperCase();
  }

  return "?";
};

/**
 * Alternative version that always returns exactly 2 characters (pads with "?" if needed)
 * @param firstName - The first name (or full name)
 * @param lastName - The last name (optional)
 * @returns Two character initials exactly (e.g., "J?" if last name is missing)
 */
export const getInitialsPadded = (
  firstName?: string | null,
  lastName?: string | null
): string => {
  const initials = getInitials(firstName, lastName);

  if (initials.length === 2) return initials;
  if (initials === "?") return "??";

  // Pad with ? if only one initial
  return initials + "?";
};
